// MNSP enrollment section for the recipient intake form.
//
// Rendered only when the operator dispatched the intake with
// mnspBundle=true (intakeApiHandler surfaces this as
// `mnspEnrollmentRequested` on the GET load response). The section
// captures equipment + service-type + shipping selection plus the
// four per-section acceptance checkboxes that satisfy the server-side
// _validate_mnsp_acceptances gate.
//
// Pricing is DERIVED from serviceType + shippingSpeed via the
// mnspPricing lookup (matching Michael's 2026-06-23 confirmation:
// fees are fixed per register, salespeople can't manipulate).
// Merchant sees the derived numbers as a read-only summary + a live
// subtotal -- no free-text fee inputs to fill in.
//
// TODO (follow-up): per Michael's "salesperson selects on the site
// survey" framing, the serviceType + shippingSpeed inputs ideally
// live on the operator-side NewApplication send-intake form, and
// this section renders them as read-only. Kept editable here for
// the initial release so the form is end-to-end testable; the
// salesperson-side move requires adding the fields to
// SendIntakeLinkInput and persisting at intake-send time.

import { useEffect, useState, type ReactNode } from "react";
import { FileText, Pencil } from "lucide-react";
import type {
  AgreementAcceptance,
  AgreementSection,
  MnspEnrollment,
  MnspServiceType,
  MnspShippingSpeed,
} from "../lib/types";
import { requiredAgreementSections } from "../lib/types";
import {
  MNSP_FEES,
  computeFeesSubtotal,
  formatUsd,
  getEffectiveMnspFees,
  getEffectiveShippingFee,
  getMnspFees,
  getShippingFee,
  getShippingOptionLabel,
  mnspShippingToPdiLabel,
  shippingSpeedChoices,
} from "../lib/mnspPricing";
import { SignaturePad } from "./SignaturePad";
import { FieldErrorText } from "./application/FormField";
import {
  feeInputSafe,
  formatAmountOnBlur,
  lettersSpaceOnly,
  stripAmountFormatting,
} from "@/lib/formatters";

// Fee inputs let the operator type a "$" for familiarity (feeInputSafe keeps
// it in the field); parseFee strips it -- plus any stray comma/whitespace --
// before parsing. Uses Number() (NOT parseFloat) so a malformed value like
// "3.4.5" resolves to NaN and is dropped as "no override" instead of being
// silently coerced to 3.4 and sent on the wire. Blank stays NaN (no value).
// Mirrors NewApplication's fee-override handling so both forms treat "$25"
// and "25" identically.
const parseFee = (raw: string | null | undefined): number => {
  const s = stripAmountFormatting(raw ?? "").trim();
  return s === "" ? NaN : Number(s);
};

// Same generous sanity cap the pricing-override fields use (FEE_OVERRIDE_MAX
// in NewApplication / _FEE_OVERRIDE_MAX in pricing_tiers.py). A fee above
// this is a fat-finger, not a real charge.
export const MNSP_FEE_MAX = 100_000;

export interface MnspFeeError {
  field: string;
  message: string;
}

// Mirrors NewApplication.classifyOverride: blank is fine (use the standard
// price), a non-numeric value is "malformed", a negative or over-cap value is
// out of range. Kept self-contained here to avoid a page<->component import
// cycle (NewApplication already imports this module).
function mnspFeeProblem(raw: string): "malformed" | "range" | null {
  const t = stripAmountFormatting(raw).trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return "malformed";
  if (n < 0 || n > MNSP_FEE_MAX) return "range";
  return null;
}

function mnspFeeMessage(
  label: string,
  raw: string,
  problem: "malformed" | "range",
): string {
  return problem === "malformed"
    ? `${label}: “${raw.trim()}” isn’t a valid number — enter a plain amount like 12.50, or leave it blank.`
    : `${label}: must be between $0 and $${MNSP_FEE_MAX.toLocaleString()}, or blank for the standard price.`;
}

// ---- Form state shape mirrors MnspEnrollment but with string inputs --------
// matching the rest of IntakeForm's pattern (number inputs are stored as
// strings while typing; parsed at submit). Acceptances are tracked as a
// map keyed by section for O(1) toggle.

export interface MnspEnrollmentFormState {
  serviceType: MnspServiceType | "";
  // Free-text register name, only meaningful when serviceType is
  // OTHER_MNSP. Empty otherwise.
  registerOther: string;
  // In-house PP terminal rental, $/month, as a string while typing.
  // Operator-set only (NewApplication); the merchant intake renders it
  // read-only and the server strips it from merchant PATCHes.
  terminalMonthlyRentalFee: string;
  // Operator override of the standard per-register deployment/monthly
  // fees. Blank = use the standard fee for the selected register; a
  // valid number (including "0", a full waiver) overrides it. Edited
  // via the pencil icon in the Pricing summary card -- operator-set
  // only (NewApplication/EditApplicationModal); the merchant intake
  // renders the effective (already-overridden) numbers but never the
  // edit affordance, and the server strips these from merchant PATCHes.
  deploymentFeeOverride: string;
  monthlyFeeOverride: string;
  gatewayName: string;
  terminalType: string;
  pinPad: string;
  internetGatewayProvided: "" | "YES" | "NO";
  shippingSpeed: MnspShippingSpeed | "";
  // Operator override of the shipping fee. Blank = use the standard
  // per-speed fee. Same edit affordance/operator-only posture as
  // deploymentFeeOverride/monthlyFeeOverride above. SECURITY: only takes
  // effect while shippingSpeed === shippingFeeOverrideSpeed below -- see
  // that field's comment.
  shippingFeeOverride: string;
  // The shipping speed shippingFeeOverride was set for, captured
  // automatically at the moment the override is typed (see the pencil
  // icon's onOverrideChange handling below). Kept separate from
  // shippingSpeed so that later changing the selected speed -- by the
  // operator or the merchant -- doesn't silently carry a cheaper day's
  // override over to a pricier one.
  shippingFeeOverrideSpeed: MnspShippingSpeed | "";
  // section -> ISO timestamp when the checkbox was ticked. Absence
  // means "not accepted". Toggling off clears the entry (acts as
  // un-acceptance).
  acceptances: Partial<Record<AgreementSection, string>>;
  // Free-text affirmation matches the signature block on page 2 of the
  // PDF -- "I acknowledge ... [Printed Name]". Used for every section
  // (single name affirms all four) to keep the UX light.
  acceptedBy: string;
  // Hand-drawn signature captured via the SignaturePad component.
  // Base64 PNG data URL; persists on MnspEnrollment.signatureImage
  // and renders on the MAR PDF via the pdf_render overlay step.
  // Empty string means "not yet signed."
  signatureImage: string;
}

export const EMPTY_MNSP_FORM: MnspEnrollmentFormState = {
  serviceType: "",
  registerOther: "",
  terminalMonthlyRentalFee: "",
  deploymentFeeOverride: "",
  monthlyFeeOverride: "",
  gatewayName: "",
  terminalType: "",
  pinPad: "",
  internetGatewayProvided: "",
  // Default to STANDARD shipping per Michael's 2026-06-23 confirmation:
  // "Default is standard shipping, no charge." Salesperson/merchant
  // explicitly picks expedited when needed.
  shippingSpeed: "STANDARD",
  shippingFeeOverride: "",
  shippingFeeOverrideSpeed: "",
  acceptances: {},
  acceptedBy: "",
  signatureImage: "",
};

export function fromServerMnsp(
  src: Record<string, unknown> | null | undefined,
): MnspEnrollmentFormState {
  if (!src) return { ...EMPTY_MNSP_FORM };
  const igProvided = src.internetGatewayProvided;
  const acceptancesArr = Array.isArray(src.acceptances)
    ? (src.acceptances as AgreementAcceptance[])
    : [];
  const acceptances: Partial<Record<AgreementSection, string>> = {};
  let lastAcceptedBy = "";
  for (const a of acceptancesArr) {
    if (a?.section && a.acceptedAt) {
      acceptances[a.section] = a.acceptedAt;
      if (a.acceptedBy) lastAcceptedBy = a.acceptedBy;
    }
  }
  return {
    serviceType: (src.serviceType as MnspServiceType) || "",
    registerOther: String(src.registerOther || ""),
    terminalMonthlyRentalFee:
      src.terminalMonthlyRentalFee === null ||
      src.terminalMonthlyRentalFee === undefined
        ? ""
        : String(src.terminalMonthlyRentalFee),
    deploymentFeeOverride:
      src.deploymentFeeOverride === null ||
      src.deploymentFeeOverride === undefined
        ? ""
        : String(src.deploymentFeeOverride),
    monthlyFeeOverride:
      src.monthlyFeeOverride === null || src.monthlyFeeOverride === undefined
        ? ""
        : String(src.monthlyFeeOverride),
    gatewayName: String(src.gatewayName || ""),
    terminalType: String(src.terminalType || ""),
    pinPad: String(src.pinPad || ""),
    internetGatewayProvided:
      igProvided === true ? "YES" : igProvided === false ? "NO" : "",
    // Default to STANDARD when server has no value -- matches the
    // EMPTY_MNSP_FORM default and Michael's "default is standard,
    // no charge" framing. Server-saved values override only when
    // explicitly set.
    shippingSpeed: (src.shippingSpeed as MnspShippingSpeed) || "STANDARD",
    shippingFeeOverride:
      src.shippingFeeOverride === null || src.shippingFeeOverride === undefined
        ? ""
        : String(src.shippingFeeOverride),
    shippingFeeOverrideSpeed:
      (src.shippingFeeOverrideSpeed as MnspShippingSpeed) || "",
    acceptances,
    acceptedBy: lastAcceptedBy,
    signatureImage:
      typeof src.signatureImage === "string" ? src.signatureImage : "",
  };
}

// mnspEnabled/rentalEnabled scrub fields belonging to whichever section is
// OFF, even if the form still holds values from before the operator
// toggled it off. Without this, unchecking "Terminal rental" (or "MNSP
// enrollment") after typing into its fields left the stale values sitting
// in mnspForm state -- hidden from the UI (the section no longer renders)
// but still serialized into the submitted payload and rendered onto the
// MAR PDF. shippingSpeed/signatureImage stay unscrubbed: they're shared
// across both sections, not owned by either.
// Named, submit-blocking validation for the operator-editable MNSP fee
// inputs, mirroring NewApplication's pricing-override validators. Each
// FieldError's `field` is the input's DOM id so the parent's focusFirstError
// / roll-up banner can scroll to it and the clear-on-fix effect (keyed on the
// field id) prunes it once fixed. Only validates a fee that is actually
// active + visible: the terminal rental fee when rentalEnabled, and the MNSP
// deployment/monthly/shipping overrides (edited in FeesSummary) when
// mnspEnabled. Blank is always fine (use the standard price).
export function mnspServiceTypeErrors(
  form: MnspEnrollmentFormState,
  mnspEnabled: boolean,
): MnspFeeError[] {
  if (!mnspEnabled) return [];
  if (!form.serviceType) {
    return [
      {
        field: "mnsp-service-type",
        message: "Select a register / service type.",
      },
    ];
  }
  if (form.serviceType === "OTHER_MNSP" && !form.registerOther.trim()) {
    return [
      {
        field: "mnsp-register-other",
        message: "Enter the register name.",
      },
    ];
  }
  return [];
}

export function mnspFeeFieldErrors(
  form: MnspEnrollmentFormState,
  mnspEnabled: boolean,
  rentalEnabled: boolean,
): MnspFeeError[] {
  const errs: MnspFeeError[] = [];
  const check = (field: string, label: string, raw: string) => {
    const problem = mnspFeeProblem(raw);
    if (problem) errs.push({ field, message: mnspFeeMessage(label, raw, problem) });
  };
  if (rentalEnabled) {
    check(
      "mnsp-rental-fee",
      "Terminal monthly rental",
      form.terminalMonthlyRentalFee,
    );
  }
  if (mnspEnabled) {
    check(
      "mnsp-deployment-fee",
      "Product deployment fee override",
      form.deploymentFeeOverride,
    );
    check(
      "mnsp-monthly-fee",
      "Monthly service & device fee override",
      form.monthlyFeeOverride,
    );
    check(
      "mnsp-shipping-fee",
      "Shipping fee override",
      form.shippingFeeOverride,
    );
  }
  return errs;
}

/**
 * The MNSP block with every pricing field cleared: the terminal monthly
 * rental fee and the fee overrides. For a caller who may not set pricing
 * (canEditPricing off), so a value that reached the form some other way (a
 * restored draft, or an edit form loaded with Payment Products' stored values)
 * is never sent: the server rejects any of these from a non-pricing role, and
 * leaving them out keeps whatever is stored.
 */
export function withoutMnspPricing<T extends Partial<MnspEnrollment>>(wire: T): T {
  return {
    ...wire,
    terminalMonthlyRentalFee: null,
    deploymentFeeOverride: null,
    monthlyFeeOverride: null,
    shippingFeeOverride: null,
    shippingFeeOverrideSpeed: null,
  };
}

export function toWireMnsp(
  form: MnspEnrollmentFormState,
  mnspEnabled: boolean,
  rentalEnabled: boolean,
): MnspEnrollment {
  const applicableSections = new Set(
    requiredAgreementSections(mnspEnabled, rentalEnabled),
  );
  const acceptances: AgreementAcceptance[] = (
    Object.entries(form.acceptances) as Array<[AgreementSection, string]>
  )
    .filter(([section, ts]) => !!ts && applicableSections.has(section))
    .map(([section, acceptedAt]) => ({
      section,
      acceptedAt,
      acceptedBy: form.acceptedBy.trim() || null,
    }));
  return {
    serviceType: mnspEnabled ? form.serviceType || null : null,
    // Only persist the free-text register name for OTHER_MNSP; the three
    // fixed registers carry their name in the pricing table, not here.
    registerOther:
      mnspEnabled && form.serviceType === "OTHER_MNSP"
        ? form.registerOther.trim() || null
        : null,
    terminalMonthlyRentalFee: rentalEnabled
      ? (() => {
          const parsed = parseFee(form.terminalMonthlyRentalFee);
          return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        })()
      : null,
    // Fee overrides accept 0 (a full waiver), unlike terminalMonthlyRentalFee
    // above -- ">= 0" not "> 0".
    deploymentFeeOverride: mnspEnabled
      ? (() => {
          const parsed = parseFee(form.deploymentFeeOverride);
          return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
        })()
      : null,
    monthlyFeeOverride: mnspEnabled
      ? (() => {
          const parsed = parseFee(form.monthlyFeeOverride);
          return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
        })()
      : null,
    gatewayName: mnspEnabled ? form.gatewayName.trim() || null : null,
    terminalType: rentalEnabled ? form.terminalType.trim() || null : null,
    pinPad: rentalEnabled ? form.pinPad.trim() || null : null,
    internetGatewayProvided: !mnspEnabled
      ? null
      : form.internetGatewayProvided === "YES"
        ? true
        : form.internetGatewayProvided === "NO"
          ? false
          : null,
    shippingSpeed: form.shippingSpeed || null,
    // Unscrubbed like shippingSpeed above: shipping is shared across both
    // sections (MNSP register and/or the rental terminal), so it's not
    // owned/gated by mnspEnabled alone. shippingFeeOverrideSpeed is sent
    // paired with the override (both null together, or both set
    // together) -- see the pencil icon's onOverrideChange handling,
    // which pins shippingFeeOverrideSpeed to whichever speed was
    // selected at the moment the override was typed. Never derived from
    // the CURRENT form.shippingSpeed here, since that could differ by
    // the time of submit (SECURITY: see shippingFeeOverride's comment).
    shippingFeeOverride: (() => {
      const parsed = parseFee(form.shippingFeeOverride);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    })(),
    shippingFeeOverrideSpeed: (() => {
      const parsed = parseFee(form.shippingFeeOverride);
      return Number.isFinite(parsed) && parsed >= 0
        ? form.shippingFeeOverrideSpeed || null
        : null;
    })(),
    signatureImage: form.signatureImage || null,
    acceptances,
  };
}

// Client-side check matching the server's _validate_mnsp_acceptances.
// Returns the human-readable labels of any missing sections. mnspEnabled
// / rentalEnabled determine which sections actually apply -- see
// requiredAgreementSections.
export function findMissingAcceptanceLabels(
  form: MnspEnrollmentFormState,
  mnspEnabled: boolean,
  rentalEnabled: boolean,
): string[] {
  return requiredAgreementSections(mnspEnabled, rentalEnabled)
    .filter((s) => !form.acceptances[s])
    .map((s) => SECTION_DEFS.find((def) => def.section === s)?.title ?? s);
}

// ---- Section definitions ---------------------------------------------------
// Single source of truth for the four acceptance rows. Title + summary
// shown next to the checkbox; `legalText` is the full PDF wording
// revealed via the "Read full agreement" disclosure. Wording lifted
// verbatim from the source PDF so the audit trail matches what the
// merchant agreed to.

interface SectionDef {
  section: AgreementSection;
  title: string;
  summary: string;
  legalText: string;
}

const SECTION_DEFS: SectionDef[] = [
  {
    section: "MNSP_BILLING",
    title: "MNSP Payment & Billing Subscription",
    summary:
      "36-month subscription with a 12-month non-cancellable rollover. 60-day written cancellation notice required before the term-end date.",
    legalText:
      "MNSP Payment & Billing Subscription Plan: Upon installation, merchant will be billed a one-time charge of $___ for installation and deployment from PPNW. Merchant agrees to a 36-month term at $___ per month per site plus applicable taxes for the MNSP services. At the end of the 36 months, the merchant will continue to be billed the $___ for a 12-month non-cancellable term per site, renewing each year on the date of this document until the merchant cancels service with PPNW for the use of the MNSP. A 60-day written notice of cancellation is required prior to the expiration of the term date on this document. Total billing will be the first month's payment plus the install/deployment fee of the MNSP per site totaling $___. This is a non-cancellable 36-month agreement. In the event the merchant closes, or sells the business, or attempts to cancel the MNSP agreement before the 36-month or 12-month term is completed, the merchant agrees to pay the remaining months in the current term of the agreement at $___ per site canceling plus applicable taxes in full as one sum to PPNW. For merchants with multiple sites, please see attached “Exhibit A” for the locations contracted under this agreement.",
  },
  {
    section: "TERMINAL_RENTAL",
    title: "Terminal Monthly Rental",
    summary:
      "6-month non-cancellable rental at $___ per month. If equipment isn't returned within 30 days of cancellation, you owe $750 per unit.",
    legalText:
      "Terminal Monthly Rental Plan: Merchant agrees to rent equipment listed in this document from PPNW for $___ per month. The merchant agrees to a 6-month non-cancellable term for the rental plan. At the end of 6 months the merchant will continue to pay the fee listed above on a monthly basis with no contractual obligation until the merchant either, purchases or leases the equipment, cancels the merchant account with PPNW, or sells/closes the business. If the merchant decides to do any of the options listed in this document, they will still be obligated to pay the fee listed above for the full 6-month term, or pay in full, in one sum, the remaining monthly amount multiplied by the remaining months of the non-cancellable term to PPNW. A 30-day written notice of cancellation is required for rental cancellation, and the merchant is required to return the rented equipment listed in this document back to PPNW within 30 days of the cancellation notice. In the event the equipment is not returned, the merchant will pay the sum of $750.00 for the equipment loss. The merchant will then be able to keep the rental equipment.",
  },
  {
    section: "ACH_AUTH",
    title: "ACH Authorization",
    summary:
      "PPNW and its affiliates may auto-debit your bank account for amounts owed under this agreement until you give written revocation.",
    legalText:
      "By signing below, Merchant authorizes PPNW and its affiliates to initiate debits entries to Merchant’s bank account designated above at the Bank listed above through an Automated Clearing House (“ACH”) network, to pay any and all amounts owing to PPNW under this Agreement, including but not limited to amounts owing for equipment, hardware, shipping, Early Termination Fees or pay-off/buyout Amounts paid to Merchant and charged back hereunder. Merchant agrees to be bound by the operating rules of the ACH operating network as in effect from time to time. Merchant agrees that this authorization of ACH debit entries will remain in effect until PPNW receives Merchant’s written notification revoking the authorization and PPNW and Banks have a reasonable opportunity to act on that written notification.",
  },
  {
    section: "CANCELLATION_TERMS",
    title: "Warranty & Cancellation Terms",
    summary:
      "Equipment is yours free and clear; rates are guidelines not guaranteed; payoff/cancellation fees apply; disputes arbitrated in Washington state.",
    legalText:
      "Merchant warrants to Payment Products NW (“PPNW”) that any equipment transferred to PPNW is owned by Merchant free and clear of all others and will indemnify and hold PPNW harmless from all claims. Merchant understands that the purchase or lease of equipment from PPNW is separate and independent from Merchant’s agreement for payment processing services. All funds paid by merchant are non-refundable and all transactions are final. Merchant has read and accepted any terms or service agreements provided with this receipt and understand that rates and processing fees are a guideline for monthly billing and in no way constitutes a guarantee of savings. Payoff & Cancellation Fees: Any amount that PPNW agrees to pay to Merchant to cover previous processing service cancellation fees or previous lease buy-outs will be paid to Merchant only after 30 days of paid processing with PPNW equipment by Merchant. After Merchant delivers paid invoices, a buy-out letter, or copies of bank statement showing these fees being paid, PPNW will then reimburse the agreed fee to the Merchant, up to the amount shown in Special Instructions above. If Merchant elects to cancel any agreements with PPNW, or the separate services of CardConnect, Merchant shall pay the early termination fee listed in the CardConnect Agreement as well as the full amount of any reimbursements paid to Merchant by PPNW above. Merchant accepts all responsibility for cancellation of any prior processing services and paying all related fees. CardConnect Cancellation Fee: If Merchant elects to cancel processing agreement it will be subject to the applicable CardConnect early termination fee. Any special Instruction reimbursement paid by PPNW must be repaid to PPNW in the event of the early termination of the PPNW or CardConnect agreement. Any equipment Lease Agreement with PPNW or its affiliates is a separate agreement from this Receipt and will remain in effect for its full term. This Acknowledgement Receipt agreement is accepted and executed at delivery of equipment by PPNW supersedes all prior agreements, discussions, verbal representations, or understanding related to the PPNW equipment, other than the CardConnect Agreement. Merchant agrees to settle all disputes by arbitration by a single arbitrator selected by the American Arbitration Association within the state of Washington. Merchant understands that arbitration does not involve settlement by a judge or jury and requires no legal representation.",
  },
];

// Gateway name is a fixed Yes / No / N/A choice, operator-set only.
const GATEWAY_NAME_OPTIONS = ["Yes", "No", "N/A"] as const;

// ---- The section component -------------------------------------------------

interface Props {
  form: MnspEnrollmentFormState;
  onChange: (next: MnspEnrollmentFormState) => void;
  // MNSP network enrollment and terminal rental are independent -- either,
  // both, or neither may be active. These control which of the two visual
  // blocks render (register/network fields + fees vs. rental fields +
  // fees) and which AgreementSection acceptances are required.
  mnspEnabled: boolean;
  rentalEnabled: boolean;
  // Aggregated section-level error string (e.g. from server response).
  // Inline acceptance errors are computed locally from form.acceptances.
  error?: string | null;
  // Per-fee validation lookup keyed by input DOM id (mnsp-rental-fee,
  // mnsp-deployment-fee, mnsp-monthly-fee, mnsp-shipping-fee). The parent
  // computes it from mnspFeeFieldErrors so the fee inputs render an inline
  // error + red border, matching the pricing-override fields.
  feeError?: (id: string) => string | undefined;
  // Operator-side (NewApplication): hide the signature pad + acceptance
  // checkboxes so the admin only fills equipment/service details.
  noSignature?: boolean;
  // Whether this caller may set MNSP pricing: the terminal monthly rental fee
  // and the deployment / monthly / shipping fee overrides (the pencils). The
  // server only accepts those from Payment Products / EMPLOYEE
  // (FEE_OVERRIDE_ROLES), so the portal turns this off for everyone else and
  // they never see a field that would fail their submit. Defaults on.
  canEditPricing?: boolean;
  // Whether the Register choice is marked required. Off on the operator's
  // send-intake path, where the recipient picks the register (and the intake
  // completion gate requires it). Defaults on.
  registerRequired?: boolean;
  // Merchant-side when admin pre-filled the equipment fields: hide the
  // editable equipment inputs and show only the pricing summary,
  // acceptance checkboxes, and signature pad.
  onlySignature?: boolean;
  // Document uploads (issue #63): required merchant documents kept on
  // file by Payment Products (billing runs off the voided check; CoPilot
  // digital-signature adjustments require the DL). Rendered only when
  // `onUpload` is provided (merchant-side intake; the operator-side
  // NewApplication usage omits it).
  uploads?: { driversLicense: boolean; voidedCheck: boolean };
  onUpload?: (
    kind: "DRIVERS_LICENSE" | "VOIDED_CHECK",
    file: File,
  ) => Promise<void>;
  // Short-lived presigned GET URLs for whatever's already on file, so each
  // upload row can render a thumbnail after a page refresh (a fresh in-
  // session pick is previewed straight from the File object). Null per kind
  // when nothing's uploaded.
  uploadPreviews?: {
    driversLicense: string | null;
    voidedCheck: string | null;
  };
  // Set true by the parent's submit validation so missing uploads
  // highlight (red border) like the other required-field errors.
  showUploadErrors?: boolean;
}

export function MnspEnrollmentSection({
  form,
  onChange,
  mnspEnabled,
  rentalEnabled,
  error,
  feeError,
  noSignature = false,
  canEditPricing = true,
  registerRequired = true,
  onlySignature = false,
  uploads,
  onUpload,
  uploadPreviews,
  showUploadErrors = false,
}: Props) {
  function patch<K extends keyof MnspEnrollmentFormState>(
    key: K,
    value: MnspEnrollmentFormState[K],
  ) {
    onChange({ ...form, [key]: value });
  }
  function toggleAcceptance(section: AgreementSection, checked: boolean) {
    const next = { ...form.acceptances };
    if (checked) {
      // Client-side ISO timestamp; server overwrites with its own stamp
      // on submit anyway, but having a value in flight survives the
      // PATCH save round-trip without becoming "no acceptance".
      next[section] = new Date().toISOString();
    } else {
      delete next[section];
    }
    onChange({ ...form, acceptances: next });
  }

  const showRentalFeeLine =
    rentalEnabled &&
    Number.isFinite(parseFee(form.terminalMonthlyRentalFee)) &&
    parseFee(form.terminalMonthlyRentalFee) > 0;

  // Parsed shipping override amount, used by the speed picker below to
  // show the custom price on the one option it's pinned to
  // (form.shippingFeeOverrideSpeed) -- see getShippingOptionLabel.
  const parsedShippingOverride = parseFee(form.shippingFeeOverride);
  const activeShippingOverrideAmount =
    Number.isFinite(parsedShippingOverride) && parsedShippingOverride >= 0
      ? parsedShippingOverride
      : null;

  return (
    <div className="space-y-4">
      <p className="text-xs text-brand-muted">
        The fields and terms below come from the Merchant Acknowledgement
        Receipt and End User Agreement.
      </p>

      {/* MNSP Enrollment section -- register/network selection and its
          processing fees. A fully separate fieldset from Terminal Rental
          below so the two fee types are never conflated (register/
          network fees here; equipment rental + shipping in the other
          section). */}
      {mnspEnabled && (
        <fieldset className="panel space-y-4 p-4">
          <legend className="px-1.5 text-sm font-semibold text-white">
            MNSP Enrollment
          </legend>
          {!onlySignature && (
            <>
              {/* Register / service-type selection. Drives all downstream
              pricing -- monthly fee, deployment fee, and the subtotal
              render off this single choice (no separate fee inputs). */}
              <div>
                <p className="label mb-2">
                  Register
                  {registerRequired && <span className="required-marker">*</span>}
                </p>
                <div
                  id="mnsp-service-type"
                  className="flex flex-col gap-2 sm:flex-row sm:flex-wrap"
                >
                  {(
                    Object.entries(MNSP_FEES) as Array<
                      [
                        Exclude<MnspServiceType, "OTHER_MNSP">,
                        (typeof MNSP_FEES)[Exclude<
                          MnspServiceType,
                          "OTHER_MNSP"
                        >],
                      ]
                    >
                  ).map(([value, row]) => (
                    <RadioPill
                      key={value}
                      name="mnsp-service-type"
                      value={value}
                      checked={form.serviceType === value}
                      onChange={(v) =>
                        patch("serviceType", v as MnspServiceType)
                      }
                      label={row.displayRegister}
                    />
                  ))}
                  {/* Oddball / small-market registers outside the big three.
                      No fixed price; the rep confirms pricing manually. */}
                  <RadioPill
                    name="mnsp-service-type"
                    value="OTHER_MNSP"
                    checked={form.serviceType === "OTHER_MNSP"}
                    onChange={(v) =>
                      patch("serviceType", v as MnspServiceType)
                    }
                    label="Other"
                  />
                </div>
                {feeError?.("mnsp-service-type") && (
                  <FieldErrorText id="mnsp-service-type-err">
                    {feeError("mnsp-service-type")}
                  </FieldErrorText>
                )}
                {form.serviceType === "OTHER_MNSP" && (
                  <div className="mt-3">
                    <TextField
                      id="mnsp-register-other"
                      label="Register name"
                      value={form.registerOther}
                      onChange={(v) => patch("registerOther", v)}
                      error={feeError?.("mnsp-register-other")}
                    />
                    <p className="mt-1 text-xs text-brand-muted">
                      Your Payment Products representative will confirm
                      pricing for this register.
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* Gateway name is operator-set only (Yes / No / N/A).
                    Editable only in the operator (noSignature) flow; the
                    merchant sees it read-only, and the server strips
                    gatewayName from merchant PATCHes so it can't be
                    changed from the intake side. */}
                {noSignature ? (
                  <SelectField
                    id="mnsp-gateway"
                    label="Gateway"
                    value={form.gatewayName}
                    onChange={(v) => patch("gatewayName", v)}
                    options={GATEWAY_NAME_OPTIONS}
                  />
                ) : (
                  <ReadOnlyField
                    label="Gateway"
                    value={form.gatewayName || "Not set"}
                  />
                )}
                <div>
                  <p className="label mb-2">Internet gateway provided?</p>
                  <div className="flex gap-2">
                    <RadioPill
                      name="mnsp-internet-gateway"
                      value="YES"
                      checked={form.internetGatewayProvided === "YES"}
                      onChange={(v) =>
                        patch("internetGatewayProvided", v as "YES" | "NO")
                      }
                      label="Yes"
                    />
                    <RadioPill
                      name="mnsp-internet-gateway"
                      value="NO"
                      checked={form.internetGatewayProvided === "NO"}
                      onChange={(v) =>
                        patch("internetGatewayProvided", v as "YES" | "NO")
                      }
                      label="No"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {onlySignature && <MnspReadOnly form={form} />}
        </fieldset>
      )}

      {/* Terminal Rental section -- equipment rental fee and shipping. A
          fully separate fieldset from MNSP Enrollment above; these are
          equipment/shipping charges, not MNSP network processing fees. */}
      {rentalEnabled && (
        <fieldset className="panel space-y-4 p-4">
          <legend className="px-1.5 text-sm font-semibold text-white">
            Terminal Rental
          </legend>
          {!onlySignature && (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <TextField
                  id="mnsp-terminal"
                  label="Terminal type"
                  value={form.terminalType}
                  onChange={(v) => patch("terminalType", v)}
                />
                <TextField
                  id="mnsp-pinpad"
                  label="Pin pad"
                  value={form.pinPad}
                  onChange={(v) => patch("pinPad", v)}
                />
              </div>

              {/* Terminal rental (issue #72). Operator-set pricing:
                  rendered as an input only in the operator (noSignature)
                  mode; the merchant sees it read-only below and the
                  server strips it from merchant PATCHes. */}
              {noSignature && canEditPricing && (
                <div className="sm:w-64">
                  <TextField
                    id="mnsp-rental-fee"
                    label="Terminal monthly rental ($/month)"
                    value={form.terminalMonthlyRentalFee}
                    onChange={(v) =>
                      patch("terminalMonthlyRentalFee", feeInputSafe(v))
                    }
                    onBlur={() =>
                      patch(
                        "terminalMonthlyRentalFee",
                        formatAmountOnBlur(
                          form.terminalMonthlyRentalFee,
                          "currency",
                        ),
                      )
                    }
                    error={feeError?.("mnsp-rental-fee")}
                  />
                  <p className="mt-1 text-xs text-brand-muted">
                    Month-to-month, six-month minimum. Billed by Payment
                    Products.
                  </p>
                </div>
              )}
            </>
          )}

          {onlySignature && <RentalReadOnly form={form} />}

          {/* Terminal rental line (issue #72). Deliberately separate
              from the MNSP total above: the rental is an ongoing monthly
              charge billed by Payment Products via ACH, not a delivery
              fee, and applies independent of MNSP enrollment. */}
          {showRentalFeeLine && (
            <div className="panel p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-brand-muted">
                  Terminal rental (monthly)
                </span>
                <span className="text-brand-text">
                  {formatUsd(parseFee(form.terminalMonthlyRentalFee))} +
                  Applicable taxes
                </span>
              </div>
              <p className="mt-1 text-xs text-brand-muted">
                Month-to-month with a six-month minimum, billed separately by
                Payment Products.
              </p>
            </div>
          )}
        </fieldset>
      )}

      {/* Shipping applies to whatever equipment ships to the site (the
          MNSP register and/or the rental terminal), so it's a shared
          section rather than owned by either block above -- shown
          whenever either is active. Styled as its own fieldset, matching
          MNSP Enrollment/Terminal Rental above, so it stands out as an
          equally-weighted section rather than a minor control. Per-option
          cost is baked into each label (e.g. "2day $275") -- or, when
          Michael has set a shipping override, replaced by the custom
          price for the ONE speed it applies to (getShippingOptionLabel);
          every other speed keeps showing its standard price, so it's
          visually obvious the discount doesn't follow a switch to a
          different (normally pricier) speed. This is the same
          shippingSpeed value the MNSP pricing summary below factors into
          its total. */}
      {(mnspEnabled || rentalEnabled) && (
        <fieldset className="panel space-y-4 p-4">
          <legend className="px-1.5 text-sm font-semibold text-white">
            Shipping
          </legend>
          {!onlySignature ? (
            <div className="flex flex-wrap gap-2">
              {shippingSpeedChoices(form.shippingSpeed).map((value) => (
                <RadioPill
                  key={value}
                  name="mnsp-shipping"
                  value={value}
                  checked={form.shippingSpeed === value}
                  onChange={(v) =>
                    patch("shippingSpeed", v as MnspShippingSpeed)
                  }
                  label={getShippingOptionLabel(
                    value,
                    activeShippingOverrideAmount,
                    form.shippingFeeOverrideSpeed,
                  )}
                />
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-between text-sm">
              <span className="text-brand-muted">Shipping</span>
              <span className="text-brand-text">
                {form.shippingSpeed
                  ? getShippingOptionLabel(
                      form.shippingSpeed,
                      activeShippingOverrideAmount,
                      form.shippingFeeOverrideSpeed,
                    )
                  : "—"}
              </span>
            </div>
          )}
        </fieldset>
      )}

      {/* Pricing summary -- read-only, derived from the Register
          selection above + the per-register lookup in mnspPricing.ts.
          Rendered below Shipping since the total factors in the
          selected shipping speed. Mirrors the Python source
          (mnsp_pricing.py) so the rendered PDF and this UI display the
          same numbers. Fees are FIXED per Michael's 2026-06-23
          confirmation -- this view is purely informational; merchants
          cannot edit fees from the form. NOTE: this total still
          includes shipping (compute_fees_subtotal on the backend does
          the same, and this figure is what carries over to the
          rendered MAR PDF). */}
      {mnspEnabled && (
        <FeesSummary
          serviceType={form.serviceType}
          shippingSpeed={form.shippingSpeed}
          registerOther={form.registerOther}
          deploymentFeeOverride={form.deploymentFeeOverride}
          monthlyFeeOverride={form.monthlyFeeOverride}
          shippingFeeOverride={form.shippingFeeOverride}
          shippingFeeOverrideSpeed={form.shippingFeeOverrideSpeed}
          feeError={feeError}
          onOverrideChange={(field, value) => {
            if (field === "shippingFeeOverride") {
              // Pin the override to whichever speed is selected right
              // now -- it will only ever apply to that one speed, even
              // if shippingSpeed changes later (SECURITY: see
              // shippingFeeOverride's comment on MnspEnrollmentFormState).
              // Clearing the input clears the pin too, so a blank
              // override never leaves a stale speed pointer behind.
              onChange({
                ...form,
                shippingFeeOverride: value,
                shippingFeeOverrideSpeed: stripAmountFormatting(value)
                  ? form.shippingSpeed
                  : "",
              });
            } else {
              patch(field, value);
            }
          }}
          editable={noSignature && canEditPricing}
        />
      )}

      {/* Required merchant documents (issue #63). Merchant-side only:
          rendered when the parent supplies the upload callback. */}
      {onUpload && uploads && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-white">
            Required documents
          </h3>
          <p className="text-xs text-brand-muted">
            Payment Products keeps these on file for billing and signature
            verification. Photos from your phone are fine (JPEG, PNG, or PDF, up
            to 10MB each).
          </p>
          <DocumentUploadRow
            label="Driver's license"
            kind="DRIVERS_LICENSE"
            uploaded={uploads.driversLicense}
            existingUrl={uploadPreviews?.driversLicense ?? null}
            onUpload={onUpload}
            highlightMissing={showUploadErrors}
          />
          <DocumentUploadRow
            label="Voided check"
            kind="VOIDED_CHECK"
            uploaded={uploads.voidedCheck}
            existingUrl={uploadPreviews?.voidedCheck ?? null}
            onUpload={onUpload}
            highlightMissing={showUploadErrors}
          />
        </div>
      )}

      {/* Per-section acceptance checkboxes -- only the sections that
          actually apply given mnspEnabled/rentalEnabled (mirrors the
          server's _required_agreement_sections). */}
      {!noSignature && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-white">
            Acknowledge each section
          </h3>
          <p className="text-xs text-brand-muted">
            Read each summary. Open the full agreement text if you'd like to
            review the legal wording before checking the box. Every section
            below must be acknowledged before you can submit.
          </p>
          {SECTION_DEFS.filter((def) =>
            requiredAgreementSections(mnspEnabled, rentalEnabled).includes(
              def.section,
            ),
          ).map((def) => (
            <AcceptanceRow
              key={def.section}
              def={def}
              accepted={!!form.acceptances[def.section]}
              onToggle={(checked) => toggleAcceptance(def.section, checked)}
            />
          ))}
          <div>
            <label
              htmlFor="mnsp-accepted-by"
              className="label mb-1.5 block"
            >
              Your printed name
              <span className="required-marker">*</span>
            </label>
            <input
              id="mnsp-accepted-by"
              type="text"
              value={form.acceptedBy}
              onChange={(e) =>
                patch("acceptedBy", lettersSpaceOnly(e.target.value))
              }
              maxLength={120}
              className="input text-sm"
              autoComplete="off"
              data-1p-ignore="true"
              data-lpignore="true"
            />
          </div>
          <div>
            <p className="label mb-1.5">
              Your signature
              <span className="required-marker">*</span>
            </p>
            <SignaturePad
              value={form.signatureImage || null}
              onChange={(dataUrl) => patch("signatureImage", dataUrl)}
              ariaLabel={(() => {
                const count = requiredAgreementSections(
                  mnspEnabled,
                  rentalEnabled,
                ).length;
                const countWord =
                  count === 1
                    ? "one"
                    : count === 2
                      ? "two"
                      : count === 3
                        ? "three"
                        : "four";
                const topic =
                  mnspEnabled && rentalEnabled
                    ? "MNSP enrollment and terminal rental"
                    : rentalEnabled
                      ? "terminal rental"
                      : "MNSP enrollment";
                return `Sign here to acknowledge the ${countWord} ${topic} section${count === 1 ? "" : "s"}`;
              })()}
              printedNameFallback={form.acceptedBy}
            />
          </div>
        </div>
      )}

      {error && <FieldErrorText className="!mt-0">{error}</FieldErrorText>}
    </div>
  );
}

// ---- Local helpers ---------------------------------------------------------

// Read-only MNSP network summary shown to the merchant when the admin
// has already populated the register/network fields. Shipping is NOT
// shown here -- it renders once, in the shared shipping block (it
// applies to whatever equipment ships, not exclusively the register).
function MnspReadOnly({ form }: { form: MnspEnrollmentFormState }) {
  const fees = getMnspFees(form.serviceType);
  const igLabel =
    form.internetGatewayProvided === "YES"
      ? "Yes"
      : form.internetGatewayProvided === "NO"
        ? "No"
        : "—";

  return (
    <div className="panel p-4">
      <h3 className="mb-2 text-sm font-medium text-brand-text">
        Network details
      </h3>
      <p className="mb-3 text-xs text-brand-muted">
        Your Payment Products representative filled in the network
        information below. Review it before signing.
      </p>
      <dl className="grid grid-cols-1 gap-y-1 text-sm sm:grid-cols-[1fr_auto] sm:gap-x-6">
        <dt className="text-brand-muted">Register</dt>
        <dd className="text-right text-brand-text">
          {form.serviceType === "OTHER_MNSP"
            ? form.registerOther.trim() || "Other"
            : (fees?.displayRegister ?? "—")}
        </dd>
        {form.gatewayName && (
          <>
            <dt className="text-brand-muted">Gateway</dt>
            <dd className="text-right text-brand-text">{form.gatewayName}</dd>
          </>
        )}
        <dt className="text-brand-muted">Internet gateway provided</dt>
        <dd className="text-right text-brand-text">{igLabel}</dd>
      </dl>
    </div>
  );
}

// Read-only rental equipment summary shown to the merchant when the
// admin has already populated the rental fields.
function RentalReadOnly({ form }: { form: MnspEnrollmentFormState }) {
  if (!form.terminalType && !form.pinPad) return null;
  return (
    <div className="panel p-4">
      <h3 className="mb-2 text-sm font-medium text-brand-text">
        Equipment details
      </h3>
      <p className="mb-3 text-xs text-brand-muted">
        Your Payment Products representative filled in the equipment
        information below. Review it before signing.
      </p>
      <dl className="grid grid-cols-1 gap-y-1 text-sm sm:grid-cols-[1fr_auto] sm:gap-x-6">
        {form.terminalType && (
          <>
            <dt className="text-brand-muted">Terminal type</dt>
            <dd className="text-right text-brand-text">{form.terminalType}</dd>
          </>
        )}
        {form.pinPad && (
          <>
            <dt className="text-brand-muted">Pin pad</dt>
            <dd className="text-right text-brand-text">{form.pinPad}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

// Billing-terms disclosures shown under the pricing summary. Wording is
// from Michael's revision on 2026-07-21
const BILLING_DISCLOSURES = [
  "The one-time product deployment fee is billed when the equipment ships to the site.",
  "The initial monthly charge is prorated from the day you receive the device through the end of the month.",
  "Shipping and the one-time product deployment fee are combined and billed as soon as the order ships.",
  "All billing related to the MNSP is processed through Payment Products. The transaction description on your bank account will show as Payment Products.",
  "To avoid service disruptions, please notify Payment Products BEFORE any bank changes, ownership changes, branding changes, or if you close your site.",
  "A $25 late fee is charged to the account for each missed recurring payment.",
] as const;

function BillingDisclosures() {
  return (
    <ul className="mt-3 list-disc space-y-1 border-t border-brand-border pl-4 pt-3 text-xs text-brand-muted">
      {BILLING_DISCLOSURES.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

function FeesSummary({
  serviceType,
  shippingSpeed,
  registerOther,
  deploymentFeeOverride,
  monthlyFeeOverride,
  shippingFeeOverride,
  shippingFeeOverrideSpeed,
  onOverrideChange,
  editable = false,
  feeError,
}: {
  serviceType: MnspServiceType | "";
  shippingSpeed: MnspShippingSpeed | "";
  registerOther?: string;
  // Override strings while typing (blank = no override). Optional so
  // callers that never pass them (none today, but keeps the component
  // usable standalone) degrade to the standard fees.
  deploymentFeeOverride?: string;
  monthlyFeeOverride?: string;
  shippingFeeOverride?: string;
  // The speed shippingFeeOverride was pinned to (set on the parent's
  // form when the override was typed -- see MnspEnrollmentSection's
  // onOverrideChange handling). SECURITY: the override only takes
  // effect while this matches the currently selected shippingSpeed --
  // see getEffectiveShippingFee.
  shippingFeeOverrideSpeed?: MnspShippingSpeed | "";
  onOverrideChange?: (
    field:
      | "deploymentFeeOverride"
      | "monthlyFeeOverride"
      | "shippingFeeOverride",
    value: string,
  ) => void;
  // Shows the pencil icon that toggles the fee rows into editable
  // inputs. Only true in the operator context (noSignature) -- the
  // merchant intake always sees the effective numbers read-only.
  editable?: boolean;
  // Per-fee validation lookup keyed by input DOM id -- see
  // MnspEnrollmentSection's Props.feeError.
  feeError?: (id: string) => string | undefined;
}) {
  const deploymentFeeErr = feeError?.("mnsp-deployment-fee");
  const monthlyFeeErr = feeError?.("mnsp-monthly-fee");
  const shippingFeeErr = feeError?.("mnsp-shipping-fee");
  // Live per-field validity, independent of the parent's submit-populated
  // feeError lookup. The form only registers feeError on a submit attempt, so
  // a value freshly typed into this edit box is not yet flagged there -- but
  // it's malformed/out-of-range the instant it's typed. Compute the problem
  // here (same mnspFeeProblem the submit validator uses, so the two can't
  // disagree) to drive both the inline error shown while editing and the Done
  // guard below: closing the editor commits the override, so Done must be
  // blocked while any shown field is invalid.
  const deploymentFeeProblem = mnspFeeProblem(deploymentFeeOverride ?? "");
  const monthlyFeeProblem = mnspFeeProblem(monthlyFeeOverride ?? "");
  const shippingFeeProblem = mnspFeeProblem(shippingFeeOverride ?? "");
  const deploymentFeeErrShown = deploymentFeeProblem
    ? mnspFeeMessage(
        "Product deployment fee override",
        deploymentFeeOverride ?? "",
        deploymentFeeProblem,
      )
    : deploymentFeeErr;
  const monthlyFeeErrShown = monthlyFeeProblem
    ? mnspFeeMessage(
        "Monthly service & device fee override",
        monthlyFeeOverride ?? "",
        monthlyFeeProblem,
      )
    : monthlyFeeErr;
  const shippingFeeErrShown = shippingFeeProblem
    ? mnspFeeMessage(
        "Shipping fee override",
        shippingFeeOverride ?? "",
        shippingFeeProblem,
      )
    : shippingFeeErr;
  // Standard branch edits all three fees; the OTHER_MNSP branch only the
  // shipping fee (deployment/monthly are rep-confirmed, not entered here).
  const blockDoneStandard = !!(
    deploymentFeeProblem ||
    monthlyFeeProblem ||
    shippingFeeProblem
  );
  const blockDoneShipping = !!shippingFeeProblem;
  const [editing, setEditing] = useState(false);
  const parsedDeploymentOverride = parseFee(deploymentFeeOverride);
  const parsedMonthlyOverride = parseFee(monthlyFeeOverride);
  const parsedShippingOverride = parseFee(shippingFeeOverride);
  const effectiveDeploymentOverride =
    Number.isFinite(parsedDeploymentOverride) && parsedDeploymentOverride >= 0
      ? parsedDeploymentOverride
      : null;
  const effectiveMonthlyOverride =
    Number.isFinite(parsedMonthlyOverride) && parsedMonthlyOverride >= 0
      ? parsedMonthlyOverride
      : null;
  const effectiveShippingOverride =
    Number.isFinite(parsedShippingOverride) && parsedShippingOverride >= 0
      ? parsedShippingOverride
      : null;
  const fees = getEffectiveMnspFees(
    serviceType,
    effectiveDeploymentOverride,
    effectiveMonthlyOverride,
  );
  const standardFees = getMnspFees(serviceType);
  const shipping = getEffectiveShippingFee(
    shippingSpeed,
    effectiveShippingOverride,
    shippingFeeOverrideSpeed,
  );
  const standardShipping = getShippingFee(shippingSpeed);
  // Only true when the override is actually pinned to the CURRENTLY
  // selected speed -- a typed-but-unpinned or pinned-to-a-different-
  // speed override must not show the "(custom)" badge or affect the
  // total, matching getEffectiveShippingFee's security check.
  const hasShippingOverride =
    effectiveShippingOverride !== null &&
    !!shippingSpeed &&
    shippingSpeed === shippingFeeOverrideSpeed;
  // A typed override that's pinned to a DIFFERENT speed than the one
  // currently selected -- surfaced in edit mode so the operator isn't
  // surprised that it silently stopped applying after a speed switch.
  const shippingOverrideMismatched =
    effectiveShippingOverride !== null &&
    !!shippingFeeOverrideSpeed &&
    shippingFeeOverrideSpeed !== shippingSpeed;
  const subtotal = computeFeesSubtotal(
    serviceType,
    shippingSpeed,
    effectiveDeploymentOverride,
    effectiveMonthlyOverride,
    effectiveShippingOverride,
    shippingFeeOverrideSpeed,
  );

  // "Other" register: no fixed price on file. Show the entered register
  // name and a clear "confirmed by your rep" state instead of numbers, so
  // the rendered packet never implies a price we don't have. Shipping is
  // still a known, selected value.
  if (serviceType === "OTHER_MNSP") {
    const tbd = "To be confirmed by your rep";
    return (
      <div className="panel p-4">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-white">
            Pricing summary
          </h3>
          {editable && (
            <button
              type="button"
              // Block Done while the shipping override is invalid: closing the
              // editor commits it, and the value would otherwise sail through
              // until a later submit rejected it.
              onClick={() => {
                if (editing && blockDoneShipping) return;
                setEditing((v) => !v);
              }}
              disabled={editing && blockDoneShipping}
              className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs ${
                editing && blockDoneShipping
                  ? "cursor-not-allowed text-brand-muted/50"
                  : "text-brand-muted hover:bg-brand-border/30 hover:text-brand-text"
              }`}
              aria-expanded={editing}
              aria-disabled={editing && blockDoneShipping}
              title={
                editing && blockDoneShipping
                  ? "Fix the shipping fee error before finishing"
                  : undefined
              }
              aria-label={
                editing ? "Done editing shipping fee" : "Edit shipping fee"
              }
            >
              <Pencil className="h-3.5 w-3.5" />
              {editing ? "Done" : "Edit"}
            </button>
          )}
        </div>
        <p className="mb-3 text-xs text-brand-muted">
          {editing
            ? `Leave blank to use the standard shipping fee. The price only applies to ${mnspShippingToPdiLabel(shippingSpeed)} shipping -- switching the speed above clears its effect.`
            : "This register isn't one of the standard three, so its fees are set by your Payment Products representative rather than fixed here."}
        </p>
        <dl className="grid grid-cols-1 gap-y-1 text-sm sm:grid-cols-[1fr_auto] sm:gap-x-6">
          <dt className="text-brand-muted">Register</dt>
          <dd className="text-right text-brand-text">
            {registerOther?.trim() || "Other"}
          </dd>
          <dt className="text-brand-muted">
            Product deployment fee (one-time)
          </dt>
          <dd className="text-right text-brand-muted">{tbd}</dd>
          <dt className="text-brand-muted">
            Managed network service & device (monthly)
          </dt>
          <dd className="text-right text-brand-muted">{tbd}</dd>
          <dt className="text-brand-muted">
            Shipping ({shipping?.label ?? "not selected"})
            {hasShippingOverride && !editing && (
              <span className="ml-1 text-brand-blue-bright">(custom)</span>
            )}
          </dt>
          {editing ? (
            <dd className="text-right">
              <ShippingOverrideInput
                id="mnsp-shipping-fee"
                value={shippingFeeOverride ?? ""}
                onChange={(v) => onOverrideChange?.("shippingFeeOverride", v)}
                placeholder={formatUsd(standardShipping?.amount ?? 0)}
                error={shippingFeeErrShown}
              />
            </dd>
          ) : (
            <dd className="text-right text-brand-text">
              {formatUsd(shipping?.amount ?? 0)}
            </dd>
          )}
        </dl>
        {editing && shippingOverrideMismatched && (
          <p className="mt-2 text-xs text-warning">
            This price is set for{" "}
            {mnspShippingToPdiLabel(shippingFeeOverrideSpeed)} shipping and
            won't apply while{" "}
            {mnspShippingToPdiLabel(shippingSpeed)} is selected above.
          </p>
        )}
        <BillingDisclosures />
      </div>
    );
  }

  if (!fees) {
    return (
      <div className="rounded-md border border-dashed border-brand-border bg-brand-bg p-3 text-xs text-brand-muted">
        Select a register above to see pricing.
      </div>
    );
  }

  const hasDeploymentOverride = effectiveDeploymentOverride !== null;
  const hasMonthlyOverride = effectiveMonthlyOverride !== null;

  return (
    <div className="panel p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-white">
          Pricing summary
        </h3>
        {editable && (
          <button
            type="button"
            // Block Done while any of the three fee overrides is invalid:
            // closing the editor commits them, and an invalid value would
            // otherwise only surface on a later submit.
            onClick={() => {
              if (editing && blockDoneStandard) return;
              setEditing((v) => !v);
            }}
            disabled={editing && blockDoneStandard}
            className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs ${
              editing && blockDoneStandard
                ? "cursor-not-allowed text-brand-muted/50"
                : "text-brand-muted hover:bg-brand-border/30 hover:text-brand-text"
            }`}
            aria-expanded={editing}
            aria-disabled={editing && blockDoneStandard}
            title={
              editing && blockDoneStandard
                ? "Fix the highlighted fee errors before finishing"
                : undefined
            }
            aria-label={
              editing
                ? "Done editing pricing"
                : "Edit deployment/monthly/shipping fees"
            }
          >
            <Pencil className="h-3.5 w-3.5" />
            {editing ? "Done" : "Edit"}
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-brand-muted">
        {editing
          ? `Leave a field blank to use the standard fee. Enter 0 to waive it. The shipping price only applies to ${mnspShippingToPdiLabel(shippingSpeed)} shipping -- switching the speed above clears its effect.`
          : "Set per site, determined by Payment Products. These values are fixed and carry over to the rendered MAR + Enrollment Agreement"}
      </p>
      <dl className="grid grid-cols-1 gap-y-1 text-sm sm:grid-cols-[1fr_auto] sm:gap-x-6">
        <dt className="text-brand-muted">Register</dt>
        <dd className="text-right text-brand-text">{fees.displayRegister}</dd>
        <dt className="text-brand-muted">
          Product deployment fee (one-time)
          {hasDeploymentOverride && !editing && (
            <span className="ml-1 text-brand-blue-bright">(custom)</span>
          )}
        </dt>
        {editing ? (
          <dd className="text-right">
            <input
              id="mnsp-deployment-fee"
              type="text"
              inputMode="decimal"
              value={deploymentFeeOverride ?? ""}
              onChange={(e) =>
                onOverrideChange?.(
                  "deploymentFeeOverride",
                  feeInputSafe(e.target.value),
                )
              }
              onBlur={() =>
                onOverrideChange?.(
                  "deploymentFeeOverride",
                  formatAmountOnBlur(deploymentFeeOverride ?? "", "currency"),
                )
              }
              placeholder={formatUsd(standardFees?.deploymentFee)}
              aria-label="Product deployment fee override"
              aria-invalid={deploymentFeeErrShown ? true : undefined}
              aria-describedby={
                deploymentFeeErrShown ? "mnsp-deployment-fee-err" : undefined
              }
              className="input w-28 px-2 py-1.5 text-right text-sm"
            />
            {deploymentFeeErrShown && (
              <FieldErrorText id="mnsp-deployment-fee-err">
                {deploymentFeeErrShown}
              </FieldErrorText>
            )}
          </dd>
        ) : (
          <dd className="text-right text-brand-text">
            {formatUsd(fees.deploymentFee)}
          </dd>
        )}
        <dt className="text-brand-muted">
          Managed network service & device (monthly)
          {hasMonthlyOverride && !editing && (
            <span className="ml-1 text-brand-blue-bright">(custom)</span>
          )}
        </dt>
        {editing ? (
          <dd className="text-right">
            <input
              id="mnsp-monthly-fee"
              type="text"
              inputMode="decimal"
              value={monthlyFeeOverride ?? ""}
              onChange={(e) =>
                onOverrideChange?.(
                  "monthlyFeeOverride",
                  feeInputSafe(e.target.value),
                )
              }
              onBlur={() =>
                onOverrideChange?.(
                  "monthlyFeeOverride",
                  formatAmountOnBlur(monthlyFeeOverride ?? "", "currency"),
                )
              }
              placeholder={formatUsd(standardFees?.monthlyFee)}
              aria-label="Managed network service & device monthly fee override"
              aria-invalid={monthlyFeeErrShown ? true : undefined}
              aria-describedby={
                monthlyFeeErrShown ? "mnsp-monthly-fee-err" : undefined
              }
              className="input w-28 px-2 py-1.5 text-right text-sm"
            />
            {monthlyFeeErrShown && (
              <FieldErrorText id="mnsp-monthly-fee-err">
                {monthlyFeeErrShown}
              </FieldErrorText>
            )}
          </dd>
        ) : (
          <dd className="text-right text-brand-text">
            {formatUsd(fees.monthlyFee)}
          </dd>
        )}
        <dt className="text-brand-muted">
          Shipping ({shipping?.label ?? "not selected"})
          {hasShippingOverride && !editing && (
            <span className="ml-1 text-brand-blue-bright">(custom)</span>
          )}
        </dt>
        {editing ? (
          <dd className="text-right">
            <ShippingOverrideInput
              id="mnsp-shipping-fee"
              value={shippingFeeOverride ?? ""}
              onChange={(v) => onOverrideChange?.("shippingFeeOverride", v)}
              placeholder={formatUsd(standardShipping?.amount ?? 0)}
              error={shippingFeeErrShown}
            />
          </dd>
        ) : (
          <dd className="text-right text-brand-text">
            {formatUsd(shipping?.amount ?? 0)}
          </dd>
        )}
        <dd></dd>
        <dd className="text-brand">+ Applicable taxes</dd>
        <dt className="mt-2 border-t border-brand-border pt-2 text-sm font-medium text-brand-text">
          Total funds due at packet delivery
        </dt>
        <dd className="mt-2 border-t border-brand-border pt-2 text-right text-sm font-semibold text-brand-blue-bright">
          {formatUsd(subtotal)}
        </dd>
      </dl>
      {editing && shippingOverrideMismatched && (
        <p className="mt-2 text-xs text-warning">
          This price is set for {mnspShippingToPdiLabel(shippingFeeOverrideSpeed)}{" "}
          shipping and won't apply while {mnspShippingToPdiLabel(shippingSpeed)}{" "}
          is selected above.
        </p>
      )}
      <BillingDisclosures />
    </div>
  );
}

// Numeric override input for the Shipping row -- shared between the
// standard-register branch and the OTHER_MNSP branch of FeesSummary
// above, since the input markup is identical regardless of register
// type. The override itself is pinned to one specific shipping speed
// (see shippingFeeOverrideSpeed) -- it does NOT apply "any day," by
// design (security: see getEffectiveShippingFee).
function ShippingOverrideInput({
  value,
  onChange,
  placeholder,
  id,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  id?: string;
  error?: string;
}) {
  return (
    <>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(feeInputSafe(e.target.value))}
        onBlur={() => onChange(formatAmountOnBlur(value, "currency"))}
        placeholder={placeholder}
        aria-label="Shipping fee override"
        aria-invalid={error ? true : undefined}
        aria-describedby={error && id ? `${id}-err` : undefined}
        className="input w-28 px-2 py-1.5 text-right text-sm"
      />
      {error && (
        <FieldErrorText id={id ? `${id}-err` : undefined}>{error}</FieldErrorText>
      )}
    </>
  );
}

function AcceptanceRow({
  def,
  accepted,
  onToggle,
}: {
  def: SectionDef;
  accepted: boolean;
  onToggle: (checked: boolean) => void;
}) {
  const [showLegal, setShowLegal] = useState(false);
  return (
    <div>
      <label className="choice-tile">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => onToggle(e.target.checked)}
          className="mt-0.5"
          aria-label={`Accept ${def.title}`}
        />
        <div className="flex-1 space-y-1">
          <p className="text-sm font-medium text-brand-text">{def.title}</p>
          <p className="text-xs text-brand-muted">{def.summary}</p>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setShowLegal((v) => !v);
            }}
            className="text-xs font-medium text-brand-blue-bright underline-offset-2 hover:underline"
            aria-expanded={showLegal}
          >
            {showLegal ? "Hide" : "Read"} full agreement
          </button>
          {showLegal && (
            <p className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-brand-border bg-brand-bg-deep p-3 text-[11px] leading-relaxed text-brand-slate">
              {def.legalText}
            </p>
          )}
        </div>
      </label>
    </div>
  );
}

function RadioPill({
  name,
  value,
  checked,
  onChange,
  label,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: (v: string) => void;
  label: ReactNode;
}) {
  return (
    <label
      className={`choice-tile !items-center !py-2 text-xs ${
        checked ? "" : "text-brand-muted"
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
      />
      <span>{label}</span>
    </label>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  onBlur,
  inputMode,
  placeholder,
  maxLength,
  error,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  inputMode?: "text" | "numeric" | "decimal";
  placeholder?: string;
  maxLength?: number;
  error?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="label mb-1.5 block">
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
        className="input text-sm"
      />
      {error && <FieldErrorText id={`${id}-err`}>{error}</FieldErrorText>}
    </div>
  );
}

// Fixed-choice dropdown styled to match TextField. Used for the operator's
// Gateway name selection (Yes / No / N/A).
function SelectField({
  id,
  label,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
}) {
  return (
    <div>
      <label htmlFor={id} className="label mb-1.5 block">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input text-sm"
      >
        <option value="">Select one…</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

// Non-editable field display, styled like TextField/SelectField. Used to
// show the merchant an operator-set value (Gateway name) they can view but
// not change.
function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="label mb-1.5 block">{label}</span>
      <p className="panel w-full px-3.5 py-2.5 text-sm text-brand-text">
        {value}
      </p>
    </div>
  );
}

// ---- Document upload row (issue #63) ----------------------------------------
// One row per document. The parent owns the uploaded flag (it round-trips
// through the server view so a refresh shows true state); this row owns only
// its transient busy/error UI. The file input accepts camera capture on mobile
// so a merchant can photograph the license. Exported so the CardConnect-only
// intake and NewApplication reuse the exact same row.

export function DocumentUploadRow({
  label,
  kind,
  uploaded,
  existingUrl = null,
  onUpload,
  highlightMissing = false,
  required = true,
  deferred = false,
}: {
  label: string;
  kind: "DRIVERS_LICENSE" | "VOIDED_CHECK";
  uploaded: boolean;
  // Presigned GET URL for whatever's already on file (survives a refresh);
  // null when nothing's uploaded. A file picked in THIS session is previewed
  // straight from the File object and takes precedence over this.
  existingUrl?: string | null;
  onUpload: (
    kind: "DRIVERS_LICENSE" | "VOIDED_CHECK",
    file: File,
  ) => Promise<void>;
  // When the parent's submit validation ran and this document is still
  // missing, highlight the row (red border + message) so it stands out
  // alongside the other required-field errors (Susan, dev 2026-07-14).
  highlightMissing?: boolean;
  // Off: no required marker and never highlighted as missing (the
  // CardConnect-only intake and NewApplication, where both are optional).
  required?: boolean;
  // The parent holds the file and uploads it later (NewApplication, which has
  // no application to upload to until it saves), so "uploaded" means attached.
  deferred?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Preview of the file the merchant picked this session (object URL, so no
  // server round trip). Revoked when replaced or on unmount.
  const [localPreview, setLocalPreview] = useState<{
    url: string;
    isPdf: boolean;
    name: string;
  } | null>(null);
  const inputId = `mnsp-upload-${kind.toLowerCase()}`;
  const showRequired = required && highlightMissing && !uploaded && !busy;

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview.url);
    };
  }, [localPreview]);

  // In-session pick wins; otherwise fall back to what's on the server. The
  // server key carries the extension before the query string.
  const preview = localPreview
    ? localPreview
    : existingUrl
      ? {
          url: existingUrl,
          isPdf: /\.pdf(?:$|\?)/i.test(existingUrl),
          name: "",
        }
      : null;

  async function handleFile(file: File | undefined) {
    if (!file || busy) return;
    setBusy(true);
    setUploadError(null);
    const nextPreview = {
      url: URL.createObjectURL(file),
      isPdf: file.type === "application/pdf",
      name: file.name,
    };
    setLocalPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return nextPreview;
    });
    try {
      await onUpload(kind, file);
    } catch (e: unknown) {
      setUploadError(
        e instanceof Error ? e.message : "Upload failed. Please try again.",
      );
      // The upload failed -- drop the preview so the row doesn't imply a
      // file is on file when it isn't.
      setLocalPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev.url);
        return null;
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    // `relative` anchors the visually-hidden (sr-only, position:absolute) file
    // input below to this row. Without it the input positions against <body>
    // at its spot in the full-length form, which, inside the portal's
    // viewport-pinned shell (PortalLayout scrolls <main>, not the window),
    // stretches the window's scroll area into a large blank band at the bottom.
    <div
      className={
        showRequired
          ? "relative rounded-lg border border-danger/60 bg-danger/5 p-3"
          : "relative panel p-3"
      }
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {preview &&
            (preview.isPdf ? (
              <a
                href={preview.url}
                target="_blank"
                rel="noreferrer"
                title="Open the uploaded PDF"
                className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded border border-brand-border bg-brand-surface text-brand-muted hover:text-brand-text"
              >
                <FileText className="h-5 w-5" aria-hidden="true" />
                <span className="mt-0.5 text-[10px] font-medium">PDF</span>
              </a>
            ) : (
              <a
                href={preview.url}
                target="_blank"
                rel="noreferrer"
                title="Open the uploaded image full size"
                className="shrink-0"
              >
                <img
                  src={preview.url}
                  alt={`${label} preview`}
                  className="h-14 w-14 rounded border border-brand-border object-cover"
                />
              </a>
            ))}
          <div className="min-w-0">
            <p className="text-sm font-medium text-brand-text">
              {label}
              {required && <span className="required-marker">*</span>}
            </p>
            <p
              className={
                showRequired
                  ? "text-xs font-medium text-danger"
                  : "text-xs text-brand-muted"
              }
              role="status"
              aria-live="polite"
            >
              {busy
                ? "Uploading..."
                : uploaded && deferred
                  ? `Attached${preview?.name ? `: ${preview.name}` : ""}. Uploads when you save or send.`
                  : uploaded
                    ? preview?.name
                      ? `Uploaded — ${preview.name}`
                      : "Uploaded — check the preview"
                    : showRequired
                      ? "Required, not uploaded yet"
                      : "Not uploaded yet"}
            </p>
          </div>
        </div>
        <label
          htmlFor={inputId}
          className={
            busy
              ? "cursor-wait rounded-lg border border-brand-border px-3 py-2 text-sm text-brand-muted"
              : "cursor-pointer rounded-lg border border-brand-blue-bright/60 px-3 py-2 text-sm font-medium text-brand-blue-bright transition-colors hover:bg-brand-blue/15"
          }
        >
          {uploaded ? "Replace" : "Upload"}
          <input
            id={inputId}
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            capture="environment"
            className="sr-only"
            disabled={busy}
            onChange={(e) => {
              void handleFile(e.target.files?.[0]);
              // Reset so picking the same file again re-fires onChange.
              e.target.value = "";
            }}
          />
        </label>
      </div>
      {uploadError && (
        <FieldErrorText className="mt-2">{uploadError}</FieldErrorText>
      )}
    </div>
  );
}
